<template>
  <q-btn
    v-if="!user && methods.oauth"
    flat
    dense
    no-caps
    size="sm"
    class="q-ml-sm"
    icon="login"
    :label="signInLabel"
    @click="startSso"
  />

  <q-btn
    v-else-if="!user"
    flat
    dense
    no-caps
    size="sm"
    class="q-ml-sm"
    icon="login"
    :label="signInLabel"
    :loading="loading && !resolved"
    disable
    title="GitLab SSO is not configured"
  />

  <q-btn-dropdown
    v-else
    flat
    dense
    no-caps
    size="sm"
    class="q-ml-sm"
    :icon="$q.screen.gt.xs ? undefined : 'account_circle'"
    :label="$q.screen.gt.xs ? user.name : undefined"
    :title="`Signed in as @${user.username}`"
  >
    <q-list dense>
      <q-item>
        <q-item-section avatar>
          <q-avatar size="28px">
            <img v-if="user.avatarUrl" :src="user.avatarUrl" alt="" />
            <q-icon v-else name="person" />
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ user.name }}</q-item-label>
          <q-item-label caption>@{{ user.username }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-separator />
      <q-item v-close-popup clickable @click="onSignOut">
        <q-item-section avatar
          ><q-icon name="logout" size="18px"
        /></q-item-section>
        <q-item-section>Sign out</q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<script lang="ts" setup>
import { Notify, useQuasar } from "quasar";
import { computed, onMounted } from "vue";

import { useCurrentUser } from "@/composables/useCurrentUser";

/**
 * Sign-in for CTF-9303. GitLab SSO only: gitlab.bfops.io authenticates through
 * SAML, so this is one click for anyone already logged in.
 *
 * Personal views (CTF-9262, 9312, 9318, 9324, 9325) read `user.username` from
 * the composable.
 */

const { user, loading, resolved, methods, loadCurrentUser, signOut } =
  useCurrentUser();

const $q = useQuasar();

/** The long label wraps to three lines on a phone header. */
const signInLabel = computed(() =>
  $q.screen.gt.xs ? "Sign in with GitLab" : "Sign in",
);

onMounted(async () => {
  await loadCurrentUser();
  reportRedirectError();
});

/**
 * The OAuth callback cannot render a dialog, so it sends the reason back on
 * the URL. Show it once, then clean the address bar.
 */
function reportRedirectError() {
  const params = new URLSearchParams(window.location.search);
  const reason = params.get("sign_in_error");
  if (!reason) return;
  Notify.create({ type: "negative", message: reason, timeout: 8000 });
  params.delete("sign_in_error");
  const query = params.toString();
  window.history.replaceState(
    {},
    "",
    window.location.pathname + (query ? `?${query}` : ""),
  );
}

/** A full navigation, not fetch: the browser has to follow GitLab's redirect. */
function startSso() {
  window.location.href = "/api/auth/gitlab/start";
}

async function onSignOut() {
  await signOut();
}
</script>
